import React, { lazy, Suspense, useCallback } from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import { createGlobalStyle } from 'styled-components';
import { Box } from '@mui/material';

import DashboardContent from '@core/dashboard/components/DashboardContent';
import DashboardBarButtons from '@core/dashboard/components/DashboardBarButtons';
import PoiPreview from '@core/dashboard/components/PoiPreview';
import LeaveRegionDialog from '@core/dashboard/components/LeaveRegionDialog';
import TeleportToRoomDialog from '@core/dashboard/components/TeleportToRoomDialog';
import AdminSettings from '@core/dashboard/components/AdminSettings';
import DolbyCameraDialog from '@core/dashboard/components/DolbyCameraDialog';
import OrientationOverlay from '@core/dashboard/components/Overlays/OrientationOverlay';
import { useShowOrientationOverlay } from '@core/dashboard/components/Overlays/OrientationOverlay/hooks';
import MapModal from '@core/dashboard/components/MapModal/MapModal';
import TeleportModal from '@core/dashboard/components/TeleportModal/TeleportModal';
import { useTeleportKeyboardShortcut } from '@core/dashboard/components/TeleportModal/hooks/useTeleportKeyboardShortcut';
import { useHiddenWhileEditing } from '@core/dashboard/hooks/useHiddenWhileEditing';
import { IEventPageView } from './interfaces';

import { useStyles } from './styles';
import { useEventContext } from '@providers/EventProvider';
import { useMytaverseEvent } from '@core/dashboard/providers';
import MobileExternalKeyboardControls from '@components/VideoGameCast/MobileExternalKeyboardControls';
import { OrientationFromTo } from '@core/dashboard/components/Overlays/OrientationOverlay/interfaces';
import ExternalKeyboardOverlay from '@core/dashboard/components/Overlays/ExternalKeyboardOverlay';
import { useMytaverse } from '@providers/MytaverseProvider';
import { CurrentScreenEnum } from '@providers/MytaverseProvider/interfaces';

import { env } from 'env';

const ZoomScreenPreview = lazy(
  () => import('@features/zoom/components/ZoomScreenPreview'),
);

const GlobalStyle = createGlobalStyle`
  body {
    background-color: #000000;
  }
`;

const EventPageView = ({ openAdminSettingsModal }: IEventPageView) => {
  const { showOverlay, isLandscape, handleHideOverlay } =
    useShowOrientationOverlay();
  const { isEventRun } = useEventContext();
  const { showExternalKeyboardOverlay, currentRoom } = useMytaverseEvent();
  const { currentScreen } = useMytaverse();

  // Hide the teleport menu while the branding editor's Inspector is open — it
  // occupies the same right-edge screen area (MV-1551). This only gates
  // rendering; the teleport zustand store is left untouched, so the modal
  // reappears in its prior open state when the Inspector closes.
  const isHiddenWhileEditing = useHiddenWhileEditing();

  const showOverlayWhenWorldIsLoaded =
    showOverlay && currentScreen === CurrentScreenEnum.WORLD;

  const classes = useStyles(
    showOverlayWhenWorldIsLoaded,
    isEventRun && !!currentRoom,
  );

  useTeleportKeyboardShortcut();

  const screenPreview = () => {
    switch (env.REACT_APP_FEATURE_THIRD_PARTY_COMMUNICATIONS_PROVIDER) {
      case 'ZOOM':
        return (
          <Suspense>
            <ZoomScreenPreview />
          </Suspense>
        );

      default:
        return (
          <>
            <video
              id="screen-share-content-video"
              style={classes.video(isDesktop)}
            ></video>
            <canvas
              id="screen-share-content-canvas"
              style={classes.video(isDesktop)}
            ></canvas>
          </>
        );
    }
  };

  const handleExternalKeyboardOverlayClick = useCallback(() => {
    if (isLandscape) {
      handleHideOverlay();
    }
  }, [isLandscape, handleHideOverlay]);

  return (
    <>
      {screenPreview()}
      {/*<SpeedTestCheck />*/}
      <Box id="home" sx={classes.homePage}>
        <DashboardBarButtons />
        <Box sx={classes.root}>
          <DashboardContent />
          <PoiPreview />
          <LeaveRegionDialog />
          <TeleportToRoomDialog />
          <DolbyCameraDialog />
          <MapModal />
          {!isHiddenWhileEditing && <TeleportModal />}
          {openAdminSettingsModal && <AdminSettings />}
        </Box>
      </Box>
      {showOverlayWhenWorldIsLoaded && (
        <OrientationOverlay
          orientation={OrientationFromTo.PORTRAIT_TO_LANDSCAPE}
        />
      )}
      {showExternalKeyboardOverlay && (
        <ExternalKeyboardOverlay
          onReconnectClick={handleExternalKeyboardOverlayClick}
          onSkipClick={handleExternalKeyboardOverlayClick}
        />
      )}
      {!isDesktop ? <MobileExternalKeyboardControls /> : ''}

      {isEventRun && isMobile && isLandscape && !showOverlay && <GlobalStyle />}
    </>
  );
};

export default EventPageView;
